import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from './components/DataTable/DataTable';
import { CONTACTS_COLUMNS } from './constants/tableColumns';
import API_BASE_URL from './config';

/**
 * ContactsTableView - Refactored Contacts table using DataTable component
 * 
 * Sprint 20: Replaces custom table code with DataTable
 * Note: Import wizard and Add Contact form are complex UIs - using ContactsList component for those
 */

export default function ContactsTableView({
  onViewContact,
  theme,
}) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchContacts();
    fetchSettings();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/contacts?sort_by=name&sort_order=asc`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_settings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Filter configuration for Contacts table
  const filterConfig = useMemo(() => {
    const contactTypes = settings.contact_types || [];
    
    return {
      searchTerm: {
        type: 'text',
        label: 'Search',
        placeholder: 'Search name, email, company...',
        className: 'col-md-4',
      },
      contact_type: {
        type: 'select',
        label: 'Contact Type',
        options: contactTypes.map(type => ({ value: type, label: type })),
        allLabel: 'All Types',
        className: 'col-md-3',
      },
    };
  }, [settings.contact_types]);

  // Handle Export to Excel
  const handleExportToExcel = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/export/contacts`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to export contacts');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Contacts exported successfully!');
    } catch (error) {
      console.error('Error exporting contacts:', error);
      alert('❌ Failed to export contacts. Check console for details.');
    }
  };

  // Handle Export to vCard
  const handleExportVcard = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/export/contacts/vcard`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to export vCard');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${new Date().toISOString().split('T')[0]}.vcf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Contacts exported to vCard successfully!');
    } catch (error) {
      console.error('Error exporting vCard:', error);
      alert('❌ Failed to export vCard. Check console for details.');
    }
  };

  // Handle Export to CSV
  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/export/contacts/csv`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Contacts exported to CSV successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('❌ Failed to export CSV. Check console for details.');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ margin: 0 }}>Contacts</h2>
        <div className="d-flex gap-2 flex-wrap">
          <button 
            className="btn btn-success"
            onClick={() => navigate('/contacts/add')}
            title="Add a new contact"
          >
            ➕ Add Contact
          </button>
          <button 
            className="btn btn-warning"
            onClick={() => navigate('/contacts/import')}
            title="Import contacts from CSV"
          >
            📥 Import CSV
          </button>
          <button 
            className="btn btn-outline-info"
            onClick={handleExportCSV}
            title="Export all contacts to CSV"
          >
            📄 Export CSV
          </button>
          <button 
            className="btn btn-outline-primary"
            onClick={handleExportToExcel}
            title="Export all contacts to Excel"
          >
            📊 Export Excel
          </button>
          <button 
            className="btn btn-outline-success"
            onClick={handleExportVcard}
            title="Export all contacts to vCard"
          >
            📇 Export vCard
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/contacts/settings')}
            title="Configure contact types and settings"
          >
            ⚙️ Settings
          </button>
        </div>
      </div>
      
      <DataTable
        columns={CONTACTS_COLUMNS}
        data={contacts}
        tableId="contacts-table"
        enableRowSelection={false}
        enableColumnResizing={true}
        enableColumnOrdering={true}
        enableFilters={true}
        enablePagination={false}
        pageSize={100}
        filterConfig={filterConfig}
        onRowClick={(row) => onViewContact(row.id)}
        className="contacts-table"
        defaultSorting={[{ id: 'name', desc: false }]} // Sort by name A-Z by default
      />
      
      <div className="alert alert-info mt-3">
        <strong>💡 Note:</strong> Click on any contact to view full details, add interactions, and manage information.
      </div>
    </div>
  );
}

